/**
 * Lưới an toàn cuối cho cơ chế tự cập nhật.
 *
 * Route được tải bằng React.lazy() nên mỗi trang là một file .chunk.js riêng, tên có hash.
 * Nếu tab mở quá lâu (bản build của nó đã bị dọn khỏi server), bấm sang trang chưa vào sẽ
 * tải chunk 404 -> ChunkLoadError -> TRẮNG TRANG. User không hiểu chuyện gì, chỉ thấy hệ thống lỗi.
 *
 * Ở đây bắt đúng lỗi đó và tự nạp lại trang MỘT LẦN (cờ trong sessionStorage chặn lặp vô hạn khi
 * nguyên nhân không phải do bản cũ). Reload xong là chạy bản mới nhất nên chunk chắc chắn tồn tại.
 *
 * Deploy giữ lại file của 3 bản gần nhất (deploy-frontend.sh) nên trường hợp này rất hiếm —
 * đây chỉ là lớp phòng thủ cuối.
 */

import React from 'react'
import { Button, Result } from 'antd'

const RELOAD_FLAG = 'chunk_error_reloaded'

/** Nhận diện lỗi tải chunk (thông điệp khác nhau giữa các trình duyệt). */
const isChunkLoadError = (error) => {
  if (!error) return false
  const name = error.name || ''
  const message = error.message || ''
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(message) ||
    /Loading CSS chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  )
}

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError(error) {
    if (!isChunkLoadError(error)) throw error // lỗi khác thì để nguyên, không nuốt
    return { failed: true }
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return

    let alreadyReloaded = false
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1'
      if (!alreadyReloaded) sessionStorage.setItem(RELOAD_FLAG, '1')
    } catch (e) {}

    // Lần đầu: tự nạp lại, user chỉ thấy trang tải lại chứ không thấy lỗi.
    if (!alreadyReloaded) window.location.reload()
  }

  componentDidMount() {
    // Vào được tới đây nghĩa là render thành công -> xoá cờ cho lần sau.
    try {
      sessionStorage.removeItem(RELOAD_FLAG)
    } catch (e) {}
  }

  render() {
    // Chỉ hiện khi đã reload một lần mà vẫn lỗi (mạng hỏng, server chết...).
    if (this.state.failed) {
      return (
        <Result
          status="warning"
          title="Could not load this page"
          subTitle="Please check your connection and try again."
          extra={
            <Button type="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      )
    }
    return this.props.children
  }
}

export default ChunkErrorBoundary
